/**
 * Cloud Operations — New Deployment wizard (3 steps).
 *
 * Per design doc §5 (POST /deployments) + Mockup §"New deployment wizard".
 *
 * Step 1 — pick an existing Deployment Target (mode/cloud/region inferred from row).
 * Step 2 — configure the deployment (name, platformVersion, domain, approval overrides).
 * Step 3 — review summary + Execute.
 *
 * Posts to /deployments. Backend creates the Deployment row and an initial
 * system_deployment_run; actual workflow execution starts when the W3 connector
 * runtime is wired (see DeploymentService.dispatchToWorkflowRuntime).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listTargets, listSystemWorkflows, createDeployment, getPlatformVersions,
} from '../../services/cloudOperationsApi';
import {
  T, inp, btn, btnPrimary, Banner, Field,
  ModePill, fmtDate, shortId,
} from './_ui';

// Deployable versions come from the server (PlatformVersionCatalog) — see
// GET /api/v1/cloud-operations/platform-versions. The old hard-coded default
// pinned 1.0.0-rc1 and went stale the moment rc2 shipped; DeploymentService
// now rejects any version outside the catalog, so a stale client pin becomes
// a 400 at submit rather than a customer silently getting an older build.

export default function NewDeploymentWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  // Wizard state
  const [targetId, setTargetId]               = useState('');
  const [workflowId, setWorkflowId]           = useState('');
  const [name, setName]                       = useState('');
  const [platformVersion, setPlatformVersion] = useState('');
  const [platformVersions, setPlatformVersions] = useState([]);
  const [domain, setDomain]                   = useState('');
  const [cpAauto, setCpAauto] = useState(false);
  const [cpBauto, setCpBauto] = useState(false);
  const [cpCauto, setCpCauto] = useState(false);

  // Data
  const [targets, setTargets] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      listTargets({ size: 100 }),
      listSystemWorkflows(),
      getPlatformVersions(),
    ]).then(([t, w, pv]) => {
      setTargets((t?.items || []).filter((x) => x.status === 'active'));
      setWorkflows(w?.items || []);
      const versions = Array.isArray(pv?.versions) ? pv.versions : [];
      setPlatformVersions(versions);
      // Adopt the server default only if the operator hasn't already picked.
      const fallback = pv?.default || (versions[0] && versions[0].version) || '';
      if (fallback) setPlatformVersion((cur) => cur || fallback);
      setLoading(false);
    }).catch((e) => {
      setError(e?.message || String(e));
      setLoading(false);
    });
  }, []);

  const target = useMemo(() => targets.find((t) => t.id === targetId), [targets, targetId]);

  // Filter workflow choices to the target's cloud (slug prefix). target carries
  // primary_region but not provider directly — we derive provider from the
  // cloud account credential bound to the target. For v1 wizard simplicity,
  // since we don't fetch the cloud account here, expose any standalone_system
  // workflow whose slug matches the operator's expected family. The backend
  // service will reject mismatches.
  const eligibleWorkflows = useMemo(() => {
    if (!target) return workflows;
    return workflows.filter((w) => {
      const slug = w.slug || '';
      // Match obvious bring-up workflows by slug pattern. AWS/Azure follow same shape.
      return /\.deploy\.bring-up-cluster$/.test(slug) || /\.bootstrap\./.test(slug);
    });
  }, [workflows, target]);

  // Auto-pick the first eligible workflow if there is exactly one (smooths the wizard for v1).
  useEffect(() => {
    if (!workflowId && eligibleWorkflows.length === 1) setWorkflowId(eligibleWorkflows[0].id);
  }, [eligibleWorkflows, workflowId]);

  async function onExecute() {
    setSubmitting(true);
    setSubmitErr(null);
    try {
      const approvalOverrides = {};
      if (cpAauto) approvalOverrides['CP-A'] = 'auto';
      if (cpBauto) approvalOverrides['CP-B'] = 'auto';
      if (cpCauto) approvalOverrides['CP-C'] = 'auto';
      const payload = {
        name,
        targetId,
        workflowId,
        platformVersion,
        domain,
        approvalOverrides,
      };
      const r = await createDeployment(payload);
      setResult(r);
    } catch (e) {
      setSubmitErr(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <Banner color={T.info} bg={T.infoBg}>Loading wizard data…</Banner>;
  if (error)   return <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>;
  if (result)  return <Done result={result} onClose={() => nav(`/cloud-operations/deployments/${result.deployment.id}`)} onList={() => nav('/cloud-operations/deployments')} />;

  return (
    <div>
      <Breadcrumb />
      <h2 style={{ margin: 0, fontSize: 22, color: T.ink, fontWeight: 700 }}>New deployment</h2>
      <p style={{ color: T.text2, fontSize: 13, marginTop: 4 }}>
        Pick a target, configure the deployment, review, execute. The backend creates the Deployment row and an initial
        workflow run; actual provisioning starts when the W3 connector runtime is wired.
      </p>

      <Stepper step={step} />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 20 }}>
        <div>
          {step === 1 && (
            <Step1 targets={targets} targetId={targetId} setTargetId={setTargetId} />
          )}
          {step === 2 && (
            <Step2
              workflows={eligibleWorkflows} workflowId={workflowId} setWorkflowId={setWorkflowId}
              name={name} setName={setName}
              platformVersion={platformVersion} setPlatformVersion={setPlatformVersion}
              domain={domain} setDomain={setDomain}
              cpAauto={cpAauto} setCpAauto={setCpAauto}
              cpBauto={cpBauto} setCpBauto={setCpBauto}
              cpCauto={cpCauto} setCpCauto={setCpCauto}
            />
          )}
          {step === 3 && (
            <Step3 target={target} workflow={workflows.find((w) => w.id === workflowId)}
                   name={name} platformVersion={platformVersion} domain={domain}
                   overrides={{ 'CP-A': cpAauto, 'CP-B': cpBauto, 'CP-C': cpCauto }}
                   submitErr={submitErr} submitting={submitting}
                   onExecute={onExecute} />
          )}
        </div>
        <SummaryPanel
          target={target}
          workflow={workflows.find((w) => w.id === workflowId)}
          name={name}
          platformVersion={platformVersion}
        />
      </div>

      <Nav
        step={step} setStep={setStep}
        canContinueStep1={!!targetId}
        canContinueStep2={!!workflowId && name.trim().length > 0 && platformVersion.trim().length > 0}
        onCancel={() => nav('/cloud-operations/deployments')}
      />
    </div>
  );
}

function Breadcrumb() {
  return (
    <div style={{ fontSize: 12, color: T.text3, marginBottom: 4 }}>
      <a href="/cloud-operations/deployments" style={{ color: T.text2 }}>Deployments</a>
      <span style={{ margin: '0 6px' }}>/</span>
      New
    </div>
  );
}

function Stepper({ step }) {
  const steps = ['Mode & target', 'Configuration', 'Review & execute'];
  return (
    <div style={{ display: 'flex', gap: 8, margin: '20px 0 24px' }}>
      {steps.map((label, idx) => {
        const n = idx + 1;
        const active = n === step;
        const done   = n < step;
        const fg = done ? T.success : active ? T.ink : T.text2;
        const bg = active ? T.infoBg : '#fff';
        const border = active ? T.ink2 : T.stroke;
        return (
          <div key={label} style={{
            flex: 1, padding: '12px 16px',
            background: bg, border: `1px solid ${border}`, borderRadius: 4,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{
              width: 22, height: 22, borderRadius: '50%',
              background: done ? T.success : active ? T.action : T.bgAlt,
              color: (done || active) ? '#fff' : T.text2,
              display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
            }}>{n}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: fg }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1 ──────────────────────────────────────────────────────────────────
function Step1({ targets, targetId, setTargetId }) {
  if (targets.length === 0) {
    return (
      <Banner color={T.warn} bg={T.warnBg}>
        No active deployment targets. Create one on the
        <strong> Targets</strong> tab first — a target binds a (mode, cloud account, region) tuple.
      </Banner>
    );
  }
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.stroke}`, borderRadius: 6, padding: 18 }}>
      <h3 style={{ margin: 0, fontSize: 14, color: T.ink, fontWeight: 600 }}>Pick a deployment target</h3>
      <p style={{ color: T.text2, fontSize: 12, marginTop: 4 }}>
        Target determines mode, cloud, region, and SLA tier for this deployment.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {targets.map((t) => {
          const selected = t.id === targetId;
          return (
            <label key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: 12,
              border: `1px solid ${selected ? T.ink2 : T.stroke}`,
              background: selected ? T.infoBg : '#fff',
              borderRadius: 6, cursor: 'pointer',
            }}>
              <input type="radio" name="target" checked={selected}
                     onChange={() => setTargetId(t.id)} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: T.ink, fontSize: 14 }}>{t.name}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 12, color: T.text2, alignItems: 'center' }}>
                  <ModePill mode={t.mode} />
                  <code>{t.primary_region}</code>
                  {t.paired_region && <code>⇄ {t.paired_region}</code>}
                  <code>{t.sla_tier}</code>
                </div>
              </div>
              <span style={{ fontSize: 11, color: T.text3 }}>{shortId(t.id)}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 2 ──────────────────────────────────────────────────────────────────
function Step2({
  workflows, workflowId, setWorkflowId,
  name, setName,
  platformVersion, setPlatformVersion,
  domain, setDomain,
  cpAauto, setCpAauto, cpBauto, setCpBauto, cpCauto, setCpCauto,
}) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.stroke}`, borderRadius: 6, padding: 18 }}>
      <h3 style={{ margin: 0, fontSize: 14, color: T.ink, fontWeight: 600 }}>Configuration</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
        <Field label="Workflow" required hint="System Deployment Workflow to run (kind=standalone_system).">
          <select style={inp} value={workflowId} onChange={(e) => setWorkflowId(e.target.value)} required>
            <option value="">— pick a workflow —</option>
            {workflows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} · v{w.version_label || w.version}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Deployment name" required>
          <input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. acme-prod" required />
        </Field>

        <Field label="Platform version" required hint="Reviewed, deployable releases only — served by the platform.">
          <select style={inp} value={platformVersion} onChange={(e) => setPlatformVersion(e.target.value)} required>
            {platformVersions.length === 0
              ? <option value="">Loading versions…</option>
              : platformVersions.map((v) => (
                  <option key={v.version} value={v.version}>{v.label || v.version}</option>
                ))}
          </select>
        </Field>

        <Field label="Domain" hint="Public hostname for this cluster — e.g. acme.app.prove7.io">
          <input style={inp} value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acme.app.prove7.io" />
        </Field>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 4 }}>
            Approval gate overrides
          </div>
          <div style={{ fontSize: 11, color: T.text2, marginBottom: 8 }}>
            Three named checkpoints in <code>gcp.deploy.bring-up-cluster</code>. PA can pre-approve any
            gate; un-overridden gates pause the workflow until a human approves.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Checkbox label="CP-A · Infra ready  (auto-approve)" checked={cpAauto} onChange={setCpAauto} />
            <Checkbox label="CP-B · Cluster ready  (auto-approve)" checked={cpBauto} onChange={setCpBauto} />
            <Checkbox label="CP-C · Secrets bound  (auto-approve)" checked={cpCauto} onChange={setCpCauto} />
          </div>
        </div>
      </div>
    </div>
  );
}
function Checkbox({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

// ─── Step 3 ──────────────────────────────────────────────────────────────────
function Step3({ target, workflow, name, platformVersion, domain, overrides, submitErr, submitting, onExecute }) {
  return (
    <div>
      <div style={{ background: '#fff', border: `1px solid ${T.stroke}`, borderRadius: 6, padding: 18, marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: T.ink, fontWeight: 600 }}>Review</h3>
        <div style={{ marginTop: 12, fontSize: 13, color: T.text }}>
          <ReviewLine label="Target"           value={target ? <code>{target.name}</code> : '—'} />
          <ReviewLine label="Mode"             value={target ? <ModePill mode={target.mode} /> : '—'} />
          <ReviewLine label="Primary region"   value={target ? <code>{target.primary_region}</code> : '—'} />
          {target?.paired_region &&
            <ReviewLine label="Paired region"   value={<code>{target.paired_region}</code>} />}
          <ReviewLine label="SLA tier"         value={target ? <code>{target.sla_tier}</code> : '—'} />
          <ReviewLine label="Workflow"         value={workflow ? <span><code>{workflow.slug || workflow.name}</code> · v{workflow.version_label || workflow.version}</span> : '—'} />
          <ReviewLine label="Deployment name"  value={<code>{name}</code>} />
          <ReviewLine label="Platform version" value={<code>{platformVersion}</code>} />
          {domain && <ReviewLine label="Domain"            value={<code>{domain}</code>} />}
          <ReviewLine label="Approval overrides"
            value={Object.entries(overrides).filter(([, v]) => v).map(([k]) => k).join(', ') || 'none (all gates manual)'} />
        </div>
      </div>

      <Banner color={T.warn} bg={T.warnBg}>
        <strong>Execution stays on the W3 seam.</strong> Clicking Execute creates the Deployment row and
        queues an initial workflow run. The connector runtime that actually calls GCP/AWS/Azure APIs
        lands in W3.x; until then the run sits at <code>in_progress · current_step="queued · awaiting W3 connector runtime"</code>.
        Use this to validate the authoring path end-to-end.
      </Banner>

      {submitErr && <Banner color={T.danger} bg={T.dangerBg}>{submitErr}</Banner>}

      <button onClick={onExecute} disabled={submitting} style={{ ...btnPrimary, padding: '10px 20px', fontSize: 14 }}>
        {submitting ? 'Executing…' : 'Execute'}
      </button>
    </div>
  );
}
function ReviewLine({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: `1px dashed ${T.stroke2}` }}>
      <div style={{ width: 180, fontSize: 11, textTransform: 'uppercase', color: T.text3, letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
      <div>{value}</div>
    </div>
  );
}

// ─── Summary panel + nav ─────────────────────────────────────────────────────
function SummaryPanel({ target, workflow, name, platformVersion }) {
  return (
    <div style={{
      background: T.bgTint, border: `1px solid ${T.stroke}`, borderRadius: 6,
      padding: 18, fontSize: 13, alignSelf: 'start', position: 'sticky', top: 16,
    }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 13, color: T.ink, fontWeight: 600 }}>Selection summary</h4>
      <Line label="Target"           value={target?.name || '—'} />
      {target && <Line label="Mode"  value={target.mode === 'prove7_hosted' ? 'Prove7 Cloud' : 'Self-Hosted'} />}
      <Line label="Region"           value={target?.primary_region || '—'} />
      {target?.paired_region && <Line label="Paired" value={target.paired_region} />}
      <Line label="SLA tier"         value={target?.sla_tier || '—'} />
      <Line label="Workflow"         value={workflow ? `${workflow.slug || workflow.name} v${workflow.version_label || workflow.version}` : '—'} />
      <Line label="Deployment name"  value={name || '—'} />
      <Line label="Platform version" value={platformVersion || '—'} />
    </div>
  );
}
function Line({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
      <div style={{ marginTop: 2, fontSize: 13, color: T.text, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function Nav({ step, setStep, canContinueStep1, canContinueStep2, onCancel }) {
  const canContinue = (step === 1 && canContinueStep1) || (step === 2 && canContinueStep2);
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
      <button onClick={onCancel} style={btn}>Cancel</button>
      <div style={{ flex: 1 }} />
      {step > 1 && <button onClick={() => setStep(step - 1)} style={btn}>← Back</button>}
      {step < 3 && (
        <button onClick={() => setStep(step + 1)} disabled={!canContinue} style={btnPrimary}>
          Continue →
        </button>
      )}
    </div>
  );
}

// ─── Done view ───────────────────────────────────────────────────────────────
function Done({ result, onClose, onList }) {
  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ margin: 0, fontSize: 22, color: T.ink, fontWeight: 700 }}>Deployment queued</h2>
      <p style={{ color: T.text2, fontSize: 13, marginTop: 6 }}>
        Deployment row created (<code>{shortId(result.deployment?.id)}</code>) and an initial workflow run is queued (
        <code>{shortId(result.initial_run?.id)}</code>). Open the detail page to watch progress
        once the connector runtime is live.
      </p>
      <Banner color={T.info} bg={T.infoBg}>{result.note}</Banner>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={onList} style={btn}>Back to list</button>
        <button onClick={onClose} style={btnPrimary}>Open deployment detail →</button>
      </div>
    </div>
  );
}
